import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api, useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Volume2, Mic, MicOff, ChevronLeft, ChevronRight, Loader2, Check, RotateCcw, ArrowRight, ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';

const StudentTTSPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [recording, setRecording] = useState(false);
  const [spokenText, setSpokenText] = useState('');
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState(null);
  const recognitionRef = useRef(null);
  const voiceSettingsRef = useRef({
    voice_name: 'aria',
    voice_speed: 1.0,
    voice_pitch: 1.0,
    voice_style: 'professional',
  });
  // Ref to the currently playing audio element so we can cancel it cleanly
  const audioRef = useRef(null);

  useEffect(() => {
    fetchTests();
    loadVoiceSettings();
    initSpeechRecognition();
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
      if (audioRef.current)       audioRef.current.pause();
    };
  }, []);

  const loadVoiceSettings = async () => {
    try {
      const res = await api.get('/voice-settings');
      voiceSettingsRef.current = res.data;
    } catch (err) {
      // Use defaults silently
    }
  };

  const initSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setSpokenText(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setRecording(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access.');
        }
      };

      recognitionRef.current.onend = () => {
        setRecording(false);
      };
    }
  };

  const fetchTests = async () => {
    try {
      const res = await api.get(`/tests?module_type=tts`);
      setTests(res.data);
    } catch (err) {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (testId) => {
    try {
      const res = await api.get(`/tests/${testId}`);
      setQuestions(res.data.questions || []);
      setSelectedTestId(testId);
      setCurrentIndex(0);
      setSpokenText('');
      setResult(null);
    } catch (err) {
      toast.error('Failed to load questions');
    }
  };

  const playTTS = async () => {
    if (!questions[currentIndex]) return;

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current._blobUrl) URL.revokeObjectURL(audioRef.current._blobUrl);
      audioRef.current = null;
    }
    window.speechSynthesis?.cancel();

    const text    = questions[currentIndex].question_text;
    const speed   = Math.max(0.5, Math.min(2.0, voiceSettingsRef.current.voice_speed || 1.0));
    const ttsMode = voiceSettingsRef.current.tts_mode || 'coqui';

    setSpeaking(true);

    // ── Browser TTS (Normal mode) ─────────────────────────────────────────
    if (ttsMode === 'browser') {
      try {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate   = speed;
        utterance.onend  = () => setSpeaking(false);
        utterance.onerror = () => { setSpeaking(false); toast.error('Could not play audio'); };
        window.speechSynthesis.speak(utterance);
      } catch {
        setSpeaking(false);
        toast.error('Browser TTS failed');
      }
      return;
    }

    // ── Coqui AI TTS (AI Based mode) ──────────────────────────────────────
    const speaker = voiceSettingsRef.current.voice_name || 'p225';
    let blobUrl = null;
    try {
      // Fetch via axios so JWT Authorization header is sent automatically.
      // Plain new Audio(url) bypasses interceptors → 401 Unauthorized.
      const response = await api.get('/tts/audio', {
        params: { text, speaker },
        responseType: 'blob',
      });
      blobUrl = URL.createObjectURL(response.data);
      const audio = new Audio(blobUrl);
      audio._blobUrl = blobUrl;
      audio.playbackRate = speed;
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        URL.revokeObjectURL(blobUrl);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setSpeaking(false);
        URL.revokeObjectURL(blobUrl);
        audioRef.current = null;
        toast.error('Could not play audio');
      };
      await audio.play();
    } catch (err) {
      setSpeaking(false);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      audioRef.current = null;
      toast.error('Audio playback failed');
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in this browser');
      return;
    }

    if (recording) {
      recognitionRef.current.stop();
    } else {
      setSpokenText('');
      setResult(null);
      recognitionRef.current.start();
      setRecording(true);
    }
  };

  const handleSubmit = async () => {
    const question = questions[currentIndex];
    if (!question || !spokenText.trim()) {
      toast.error('Please record yourself reading the text first');
      return;
    }

    // Stop recording if still active
    if (recording && recognitionRef.current) {
      recognitionRef.current.stop();
      setRecording(false);
    }

    setSubmitting(true);
    try {
      const res = await api.post('/attempts', {
        question_id: question.id,
        answer: spokenText.trim(),
        time_taken: 0
      });
      
      setResult({
        score: res.data.score,
        correctAnswer: res.data.correct_answer
      });
      
      if (res.data.score >= 80) {
        toast.success(`Excellent! Score: ${res.data.score.toFixed(1)}%`);
      } else if (res.data.score >= 50) {
        toast.info(`Good try! Score: ${res.data.score.toFixed(1)}%`);
      } else {
        toast.warning(`Keep practicing! Score: ${res.data.score.toFixed(1)}%`);
      }
    } catch (err) {
      toast.error('Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const goToNext = () => {
    setSpokenText('');
    setResult(null);
    setCurrentIndex(prev => prev + 1);
  };

  const goToPrev = () => {
    setSpokenText('');
    setResult(null);
    setCurrentIndex(prev => prev - 1);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 50) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-emerald-500/20 border-emerald-500';
    if (score >= 50) return 'bg-amber-500/20 border-amber-500';
    return 'bg-red-500/20 border-red-500';
  };

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;
  const hasSpeechRecognition = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Show test cards if no test is selected
  if (!selectedTestId) {
    if (tests.length === 0) {
      return (
        <DashboardLayout>
          <div className="max-w-2xl mx-auto text-center py-12">
            <Volume2 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No Reading Tests Available
            </h2>
            <p className="text-slate-500">
              Your teacher hasn't assigned any reading tests yet.
            </p>
          </div>
        </DashboardLayout>
      );
    }

    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
              Reading Tests
            </h1>
            <p className="text-slate-500">
              Read the text aloud and get scored on pronunciation accuracy
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tests.map((test) => {
              const isLocked = !!test.is_locked;
              return (
              <Card key={test.id} className={`module-card h-full group transition-shadow ${isLocked ? 'opacity-75' : 'cursor-pointer hover:shadow-lg'}`} onClick={() => !isLocked && fetchQuestions(test.id)}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                      {test.test_number}
                    </span>
                    {isLocked && (
                      <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                        🔒 Locked
                      </span>
                    )}
                  </div>
                  <div className="inline-flex p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 mb-3 group-hover:scale-110 transition-transform">
                    <Mic className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                    {test.test_name}
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">{test.question_count || 0} sentences</p>
                  {isLocked ? (
                    <p className="text-xs text-amber-600 mt-2">This test is locked. You cannot attempt it.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-slate-500">Attempts: {test.attempts || 0}</span>
                        <span className={`font-semibold ${(test.best_score || 0) >= 80 ? 'text-emerald-600' : (test.best_score || 0) >= 50 ? 'text-amber-600' : 'text-slate-600'}`}>
                          Best: {test.best_score || 0}%
                        </span>
                      </div>
                      <div className="mt-4 flex items-center text-sky-600 dark:text-sky-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                        Start Test <ArrowRight className="h-4 w-4 ml-1" />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (questions.length === 0) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-12">
          <Volume2 className="h-16 w-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            No Questions Available
          </h2>
          <p className="text-slate-500">
            Failed to load questions for this test.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedTestId(null);
              setQuestions([]);
              setCurrentIndex(0);
              setSpokenText('');
              setResult(null);
            }}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
        </div>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            Reading Test
          </h1>
          <p className="text-slate-500">
            Read the text aloud and submit to get your score
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-slate-500">
            <span>Progress</span>
            <span>{currentIndex + 1} of {questions.length}</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Question Card */}
        <Card className="overflow-hidden">
          {/* Listen Button Area */}
          <div className="bg-gradient-to-r from-sky-500 to-indigo-600 p-6">
            <div className="flex items-center justify-center gap-4">
              <Button
                size="lg"
                variant="secondary"
                onClick={playTTS}
                disabled={speaking}
                className="h-16 w-16 rounded-full"
                data-testid="play-audio-btn"
              >
                <Volume2 className={`h-8 w-8 ${speaking ? 'animate-pulse' : ''}`} />
              </Button>
              <span className="text-white text-lg font-medium">
                {speaking ? 'Playing...' : 'Click to listen'}
              </span>
            </div>
          </div>
          
          <CardContent className="p-8">
            {/* Text to Read */}
            <div className="mb-6">
              <p className="text-sm text-slate-500 mb-2">Read this text aloud:</p>
              <p className="speech-display text-slate-900 dark:text-white text-xl" data-testid="tts-text-display">
                {currentQuestion?.question_text}
              </p>
            </div>

            {/* Recording Section */}
            <div className="border-t pt-6">
              <div className="flex flex-col items-center gap-4">
                {/* Microphone Button */}
                <Button
                  size="lg"
                  onClick={toggleRecording}
                  disabled={!hasSpeechRecognition}
                  className={`h-20 w-20 rounded-full transition-all ${
                    recording 
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                  data-testid="record-btn"
                >
                  {recording ? (
                    <MicOff className="h-10 w-10" />
                  ) : (
                    <Mic className="h-10 w-10" />
                  )}
                </Button>
                <span className="text-sm text-slate-500">
                  {!hasSpeechRecognition 
                    ? 'Speech recognition not supported' 
                    : recording 
                      ? 'Recording... Click to stop' 
                      : 'Click microphone to start reading'
                  }
                </span>

                {/* Spoken Text Display */}
                {spokenText && (
                  <div className="w-full mt-4 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <p className="text-sm text-slate-500 mb-2">What you said:</p>
                    <p className="text-slate-900 dark:text-white" data-testid="spoken-text">
                      {spokenText}
                    </p>
                  </div>
                )}

                {/* Result Display */}
                {result && (
                  <div className={`w-full mt-4 p-6 rounded-lg border-2 ${getScoreBgColor(result.score)}`}>
                    <div className="text-center">
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">Your Score</p>
                      <p className={`text-5xl font-bold ${getScoreColor(result.score)}`} data-testid="score-display">
                        {result.score.toFixed(1)}%
                      </p>
                      <p className="text-sm mt-2 text-slate-500">
                        {result.score >= 80 
                          ? 'Excellent pronunciation!' 
                          : result.score >= 50 
                            ? 'Good job! Keep practicing.' 
                            : 'Keep practicing to improve.'
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                {spokenText && !result && (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || !spokenText.trim()}
                    className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="submit-btn"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Submit for Scoring
                  </Button>
                )}

                {/* Try Again Button */}
                {result && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSpokenText('');
                      setResult(null);
                    }}
                    className="mt-2"
                    data-testid="try-again-btn"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={goToPrev}
            disabled={currentIndex === 0}
            data-testid="prev-question-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={goToNext}
            disabled={currentIndex === questions.length - 1}
            data-testid="next-question-btn"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default StudentTTSPage;
